import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { map } from 'rxjs/operators';

import { STRAPI_CONFIG } from './strapi.config';
import { stripHiddenSections } from '../utils/strip-hidden-sections.util';

/**
 * Strip Strapi dynamic-zone entries flagged `hidden: true` from the HTTP
 * response body BEFORE Angular's HTTP transfer cache captures it.
 *
 * Why an interceptor (not a service-level `map`):
 *
 * `provideClientHydration(withHttpTransferCacheOptions())` installs its
 * own HttpInterceptorFn that serialises the final HttpResponse body into
 * TransferState, which is rendered to the client as `<script id="ng-state">`.
 * A `.pipe(map(...))` on HttpClient.get() runs AFTER the whole interceptor
 * chain has completed, so it cannot influence what the transfer-cache
 * interceptor stored. By filtering inside an interceptor that runs earlier
 * on the response path than the transfer-cache interceptor, the cache
 * captures the already-filtered body.
 *
 * Order matters: this interceptor must run on the response BEFORE the
 * transfer-cache interceptor writes to TransferState. Angular processes
 * response handlers in REVERSE registration order, and the transfer-cache
 * interceptor is registered via `provideClientHydration(...)` which sits
 * BEFORE `provideHttpClient(withInterceptors([...]))` in the providers
 * array — so user interceptors run AFTER the transfer cache on the
 * request path and BEFORE it on the response path. That is exactly the
 * ordering this fix needs.
 *
 * Scope: only Strapi URLs are transformed; every other HTTP call passes
 * through untouched. The filter handles all three Strapi response shapes:
 *   1. `{ data: entry }`         — findOne / findBySlug / findByCustomSlug
 *   2. `{ data: [entry, ...] }`  — collection queries (findBySlug filters,
 *                                   findMany, archive queries)
 *   3. raw `entry`               — `/api/resolve` returns the union object
 *                                   at the top level, no `data` envelope.
 *
 * `stripHiddenSections` is idempotent and safe on non-entry payloads
 * (objects without section arrays are returned untouched), so this
 * interceptor is safe on ANY Strapi response shape, including
 * `/api/redirects`, `/api/navigation`, `/api/global`, etc.
 */
export const stripHiddenSectionsInterceptor: HttpInterceptorFn = (req, next) => {
  const config = inject(STRAPI_CONFIG);

  if (!req.url.startsWith(config.baseUrl)) {
    return next(req);
  }

  return next(req).pipe(
    map((event) => {
      if (!(event instanceof HttpResponse)) {
        return event;
      }
      const body = event.body;
      if (!body || typeof body !== 'object') {
        return event;
      }

      const envelope = body as { data?: unknown };

      if (Array.isArray(envelope.data)) {
        return event.clone({
          body: {
            ...(body as Record<string, unknown>),
            data: envelope.data.map((entry) => stripHiddenSections(entry)),
          },
        });
      }

      if (envelope.data !== undefined && envelope.data !== null) {
        return event.clone({
          body: {
            ...(body as Record<string, unknown>),
            data: stripHiddenSections(envelope.data),
          },
        });
      }

      return event.clone({ body: stripHiddenSections(body) });
    }),
  );
};
